/**
 * Imprint Scene (Legal Information)
 *
 * Displays legal information, developer contact details, and privacy policy.
 * Satisfies EU/German Impressum requirements for free, ad-free games.
 */

import Phaser from 'phaser';
import { GlassButton } from '../ui/components/GlassButton';
import { MenuParticleSystem } from '../ui/effects/MenuParticleSystem';
import { UI_COLORS, TYPOGRAPHY, Z_INDEX } from '../config/designSystem';
import packageJson from '../../package.json';

export class ImprintScene extends Phaser.Scene {
  private contentContainer!: Phaser.GameObjects.Container;
  private isDragging: boolean = false;
  private dragStartY: number = 0;
  private contentStartY: number = 0;
  private minScrollY: number = 0;
  private maxScrollY: number = 0;

  // Obfuscated contact data (Base64 encoded to protect from crawlers)
  private readonly OBFUSCATED_DATA = {
    name: 'Q2hyaXN0aWFuIEthdWxpY2g=',
    email: 'aGlAYmxvYm8uYXBw',
    address: 'QW0gR2VtZWluZGVoYXVzIDEyCjQ0MjI1IERvcnRtdW5kCkdlcm1hbnk=',
  };

  constructor() {
    super({ key: 'ImprintScene' });
  }

  /**
   * Decode Base64 obfuscated string (anti-crawler protection)
   */
  private decode(encoded: string): string {
    try {
      return atob(encoded);
    } catch {
      return '[Error decoding]';
    }
  }

  create(): void {
    const centerX = this.cameras.main.centerX;
    const height = this.cameras.main.height;

    // Dark background matching main menu
    this.cameras.main.setBackgroundColor('#0A0E27');

    // Add floating particles
    new MenuParticleSystem(this);

    // Create scrollable content container
    this.contentContainer = this.add.container(0, 0);
    this.contentContainer.setDepth(Z_INDEX.uiContent);

    // Title
    const title = this.add.text(centerX, 40, 'IMPRINT', {
      fontFamily: TYPOGRAPHY.fonts.display,
      fontSize: `${TYPOGRAPHY.sizes.title}px`,
      color: UI_COLORS.primary,
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 4,
      resolution: Math.max(2, window.devicePixelRatio) * 2,
    });
    title.setOrigin(0.5);
    this.contentContainer.add(title);

    // Content
    const contentY = 120;
    const lineHeight = 35;
    let currentY = contentY;

    // Developer/Publisher (decoded at runtime)
    this.createInfoRow(
      centerX,
      currentY,
      '👤 Developer',
      this.decode(this.OBFUSCATED_DATA.name),
      true
    );
    currentY += lineHeight * 1.8;

    // Address (decoded at runtime)
    this.createInfoRow(centerX, currentY, '🏠 Address', this.decode(this.OBFUSCATED_DATA.address));
    currentY += lineHeight * 2.8;

    // Contact (decoded at runtime)
    this.createInfoRow(centerX, currentY, '📧 Contact', this.decode(this.OBFUSCATED_DATA.email));
    currentY += lineHeight * 1.8;

    // Version
    this.createInfoRow(centerX, currentY, '📦 Version', `v${packageJson.version}`);
    currentY += lineHeight * 1.8;

    // Copyright (decoded at runtime)
    this.createInfoRow(
      centerX,
      currentY,
      '©',
      `${new Date().getFullYear()} ${this.decode(this.OBFUSCATED_DATA.name)}\nAll rights reserved`
    );
    currentY += lineHeight * 2.5;

    // Privacy Policy Section
    const privacyTitle = this.add.text(centerX, currentY, 'PRIVACY POLICY', {
      fontFamily: TYPOGRAPHY.fonts.display,
      fontSize: `${TYPOGRAPHY.sizes.heading}px`,
      color: UI_COLORS.primary,
      fontStyle: 'bold',
      resolution: Math.max(2, window.devicePixelRatio) * 2,
    });
    privacyTitle.setOrigin(0.5);
    this.contentContainer.add(privacyTitle);
    currentY += lineHeight * 1.5;

    // Privacy content (with decoded contact information)
    const privacyContent = [
      {
        heading: '1. Data Controller',
        text: `${this.decode(this.OBFUSCATED_DATA.name)}, ${this.decode(this.OBFUSCATED_DATA.address).replace(/\n/g, ', ')}\nEmail: ${this.decode(this.OBFUSCATED_DATA.email)}`,
      },
      {
        heading: '2. Data Collection',
        text:
          'This game collects minimal data for leaderboard functionality:\n' +
          '• Username (chosen by you)\n' +
          '• Game completion time\n' +
          '• Best height reached\n' +
          '• Number of collectibles collected\n' +
          '• Medal earned (Bronze/Silver/Gold/Platinum)\n\n' +
          'No personal identifying information is collected.\n' +
          'No IP addresses, device IDs, or location data is stored.',
      },
      {
        heading: '3. Purpose',
        text:
          'Data is collected solely to provide global leaderboard\n' +
          'functionality, allowing players to compare scores.\n' +
          'Your username is publicly visible on the leaderboard.',
      },
      {
        heading: '4. Data Storage',
        text:
          'Data is stored securely on Upstash Redis servers\n' +
          '(Frankfurt, Germany - EU data protection).\n' +
          'Data is retained indefinitely for leaderboard persistence.\n' +
          'No data is shared with third parties.',
      },
      {
        heading: '5. Your Rights',
        text:
          'You have the right to:\n' +
          '• Access your data\n' +
          '• Request deletion of your data\n' +
          '• Correct inaccurate data\n\n' +
          `To exercise these rights, contact: ${this.decode(this.OBFUSCATED_DATA.email)}`,
      },
      {
        heading: '6. Analytics & Tracking',
        text:
          'This game does NOT use:\n' +
          '• Cookies\n' +
          '• Third-party analytics (Google Analytics, etc.)\n' +
          '• Advertising networks\n' +
          '• Tracking pixels or beacons',
      },
      {
        heading: "7. Children's Privacy",
        text:
          'This game is suitable for all ages.\n' +
          'We do not knowingly collect data from children under 13.\n' +
          'Parental supervision is recommended for young players.',
      },
      {
        heading: '8. Changes to Policy',
        text:
          'Policy updates will be posted here.\n' +
          'Continued use constitutes acceptance of changes.\n\n' +
          'Last updated: ' +
          new Date().toLocaleDateString('en-GB'),
      },
    ];

    privacyContent.forEach(section => {
      // Section heading
      const headingText = this.add.text(centerX, currentY, section.heading, {
        fontFamily: TYPOGRAPHY.fonts.josefin,
        fontSize: `${TYPOGRAPHY.sizes.body}px`,
        color: UI_COLORS.accent,
        fontStyle: 'bold',
        resolution: Math.max(2, window.devicePixelRatio) * 2,
      });
      headingText.setOrigin(0.5);
      this.contentContainer.add(headingText);
      currentY += lineHeight * 0.9;

      // Section text
      const contentText = this.add.text(centerX, currentY, section.text, {
        fontFamily: TYPOGRAPHY.fonts.josefin,
        fontSize: `${TYPOGRAPHY.sizes.tiny}px`,
        color: '#D1D5DB',
        align: 'center',
        lineSpacing: 3,
        wordWrap: { width: 340 },
        resolution: Math.max(2, window.devicePixelRatio) * 2,
      });
      contentText.setOrigin(0.5, 0);
      this.contentContainer.add(contentText);
      currentY += contentText.height + lineHeight * 1.2;
    });

    // Add bottom padding
    currentY += 80;

    // Calculate scroll limits
    const contentHeight = currentY;
    this.maxScrollY = 0;
    this.minScrollY = Math.min(0, height - contentHeight - 80);

    // Setup scrolling
    this.setupScrolling();

    // Fixed back button (not in scrollable container)
    const backButton = new GlassButton(this, centerX, height - 50, {
      text: '← BACK',
      fontSize: 20,
      width: 180,
      height: 52,
      style: 'secondary',
      onClick: () => {
        this.scene.start('MainMenuScene');
      },
    });
    backButton.setDepth(Z_INDEX.modal);
    backButton.setScrollFactor(0);
  }

  /**
   * Create an info row with label and value
   */
  private createInfoRow(
    centerX: number,
    y: number,
    label: string,
    value: string,
    highlight: boolean = false
  ): void {
    const labelText = this.add.text(centerX, y, label, {
      fontFamily: TYPOGRAPHY.fonts.josefin,
      fontSize: `${TYPOGRAPHY.sizes.caption}px`,
      color: UI_COLORS.accent,
      fontStyle: 'bold',
      resolution: Math.max(2, window.devicePixelRatio) * 2,
    });
    labelText.setOrigin(0.5);
    this.contentContainer.add(labelText);

    const valueText = this.add.text(centerX, y + 22, value, {
      fontFamily: TYPOGRAPHY.fonts.josefin,
      fontSize: `${TYPOGRAPHY.sizes.caption}px`,
      color: highlight ? '#FFFFFF' : '#D1D5DB',
      align: 'center',
      lineSpacing: 3,
      resolution: Math.max(2, window.devicePixelRatio) * 2,
    });
    valueText.setOrigin(0.5, 0);
    this.contentContainer.add(valueText);
  }

  /**
   * Setup scrolling behavior
   */
  private setupScrolling(): void {
    const { width, height } = this.cameras.main;

    // Create invisible full-screen overlay for drag detection
    const dragArea = this.add.rectangle(0, 0, width, height, 0x000000, 0.01);
    dragArea.setOrigin(0, 0);
    dragArea.setScrollFactor(0);
    dragArea.setDepth(Z_INDEX.uiBackground);
    dragArea.setInteractive();

    dragArea.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      this.isDragging = true;
      this.dragStartY = pointer.y;
      this.contentStartY = this.contentContainer.y;
    });

    this.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (this.isDragging) {
        const deltaY = pointer.y - this.dragStartY;
        const newY = this.contentStartY + deltaY;
        this.contentContainer.y = Phaser.Math.Clamp(newY, this.minScrollY, this.maxScrollY);
      }
    });

    this.input.on('pointerup', () => {
      this.isDragging = false;
    });
  }
}
